import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import type { Report } from "../../../../../../../../api/types/svTypes.ts";
import { useEnhancedBiApiClientProvider } from "../../../../contexts/ApiClientProviderContext";
import useCurrentReportConfiguration from "../../../../hooks/useCurrentReportConfiguration";
import type { AppDispatch } from "../../../../store/store";
import { setEditReport } from "../../../../store/thunks/currentReportThunks";
import { getPreconditionFailedMessage } from "../../../../utilities/getPreconditionFailedMessage.ts";

const SAVE_ERROR_MESSAGE = "An error occurred while saving the report.";
interface Props {
  report: Report;
  onClose: () => void;
}

const useSaveReportDialog = ({ report, onClose }: Props) => {
  const { updateReport, addReport, getEditReportUrl } = useEnhancedBiApiClientProvider();
  const { getCurrentReportConfigurationAsync } = useCurrentReportConfiguration();

  const { sendNotification } = useNotificationContext();
  const dispatch: AppDispatch = useDispatch();

  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);

  const onSaveReport = useCallback(
    async (name: string, groupId: string | undefined) => {
      const reportConfiguration = await getCurrentReportConfigurationAsync();
      if (reportConfiguration === undefined) {
        return;
      }

      const newReport = structuredClone(report);
      newReport.name = name;
      newReport.groupId = groupId;
      newReport.configuration = reportConfiguration;

      setSaving(true);

      updateReport(newReport)
        .then((result) => {
          if (result.success && result.data !== undefined) {
            sendNotification("The report was successfully saved.");
            dispatch(setEditReport(result.data));
            onClose();
          } else {
            setError(result.error?.message || SAVE_ERROR_MESSAGE);
          }
        })
        .catch((error: unknown) => {
          setError(getPreconditionFailedMessage(error) ?? SAVE_ERROR_MESSAGE);
        })
        .finally(() => setSaving(false));
    },
    // eslint-disable-next-line @eslint-react/exhaustive-deps
    [getCurrentReportConfigurationAsync, updateReport, addReport, getEditReportUrl, report]
  );

  return {
    error,
    saving,
    onSaveReport,
  };
};

export default useSaveReportDialog;
